import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorResponseSchema, successResponseSchema } from '../schemas/common.schemas';

const c = initContract();

const toolSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  agentType: z.string().nullable(),
  endpointUrl: z.string().url(),
  httpMethod: z.string(),
  bodySchema: z.record(z.unknown()),
  headers: z.record(z.string()).nullable(),
  enabled: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

const toolExecutionResponseSchema = z.object({
  success: z.boolean(),
  statusCode: z.number(),
  data: z.unknown().optional(),
  error: z.string().optional(),
});

export const toolsContract = c.router({
  createTool: {
    method: 'POST',
    path: '/tools',
    body: z.object({
      name: z.string().min(1).max(100),
      description: z.string().min(1).max(500),
      endpointUrl: z.string().url(),
      httpMethod: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']).optional(),
      bodySchema: z.record(z.unknown()),
      headers: z.record(z.string()).optional(),
      agentType: z.string().optional(),
    }),
    responses: {
      201: toolSchema,
      400: errorResponseSchema,
      401: errorResponseSchema,
    },
    summary: 'Register a new external tool',
  },
  listTools: {
    method: 'GET',
    path: '/tools',
    query: z.object({
      agentType: z.string().optional(),
      enabled: z.string().optional(),
    }),
    responses: {
      200: z.object({
        tools: z.array(toolSchema),
      }),
      401: errorResponseSchema,
    },
    summary: 'List all registered tools',
  },
  getTool: {
    method: 'GET',
    path: '/tools/:id',
    pathParams: z.object({
      id: z.string().uuid(),
    }),
    responses: {
      200: toolSchema,
      404: errorResponseSchema,
      401: errorResponseSchema,
    },
    summary: 'Get tool by ID',
  },
  updateTool: {
    method: 'PATCH',
    path: '/tools/:id',
    pathParams: z.object({
      id: z.string().uuid(),
    }),
    body: z.object({
      name: z.string().min(1).max(100).optional(),
      description: z.string().min(1).max(500).optional(),
      endpointUrl: z.string().url().optional(),
      httpMethod: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']).optional(),
      bodySchema: z.record(z.unknown()).optional(),
      headers: z.record(z.string()).optional(),
      agentType: z.string().nullable().optional(),
      enabled: z.boolean().optional(),
    }),
    responses: {
      200: toolSchema,
      404: errorResponseSchema,
      400: errorResponseSchema,
      401: errorResponseSchema,
    },
    summary: 'Update tool configuration',
  },
  deleteTool: {
    method: 'DELETE',
    path: '/tools/:id',
    pathParams: z.object({
      id: z.string().uuid(),
    }),
    body: z.object({}),
    responses: {
      200: successResponseSchema,
      404: errorResponseSchema,
      401: errorResponseSchema,
    },
    summary: 'Delete a tool',
  },
  executeTool: {
    method: 'POST',
    path: '/tools/:id/execute',
    pathParams: z.object({
      id: z.string().uuid(),
    }),
    body: z.object({
      body: z.record(z.unknown()).optional(),
    }),
    responses: {
      200: toolExecutionResponseSchema,
      400: errorResponseSchema,
      404: errorResponseSchema,
      401: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Execute a registered tool',
  },
});
